import type { Category, Todo } from "../../domain/entities.ts";
import type { Root } from "../../domain/app-types.ts";
import { calendarPeriodLabel, projectedCalendarDates, taskTimeRange } from "../calendar/presentation-controller.ts";
import type { CalendarMode } from "../calendar/view.ts";
import { calendarRangeDates, isoWeekNumber, type WeekStart } from "../tasks/calendar-presentation.ts";
import { addDays, addMonths, dateInTimezone } from "../tasks/recurrence.ts";
import { taskIsArchived } from "../tasks/task-visibility.ts";
import { escapeHtml } from "../../ui/text.ts";

export interface DashboardCalendarInput {
  tasks: Todo[];
  categories: Category[];
  roots: Root[];
  anchor: string;
  mode: CalendarMode;
  weekStart: WeekStart;
  weekNumbers: boolean;
  recurrenceProjections: boolean;
  timezone: string;
  today: string;
  formatDate(value: string): string;
  icon(name: string): string;
}

interface CalendarEntry { task: Todo; projected: boolean }

export function normalizeDashboardCalendarMode(value: string): CalendarMode {
  return value === "day" || value === "month" ? value : "week";
}

export function moveDashboardCalendar(anchor: string, mode: CalendarMode, direction: -1 | 0 | 1, today: string): string {
  if (direction === 0) return today;
  return mode === "month" ? addMonths(anchor, direction) : addDays(anchor, direction * (mode === "week" ? 7 : 1));
}

export function dashboardCalendarEntries(input: DashboardCalendarInput): Map<string, CalendarEntry[]> {
  const dates = calendarRangeDates(input.anchor, input.mode, input.weekStart);
  const start = dates[0], end = dates.at(-1)!;
  const entries = new Map<string, CalendarEntry[]>();
  const add = (date: string, task: Todo, projected: boolean) => {
    if (date < start || date > end) return;
    entries.set(date, [...(entries.get(date) || []), { task, projected }]);
  };
  for (const task of input.tasks) {
    if (task.deleted || taskIsArchived(task, input.categories)) continue;
    const date = task.startAt ? dateInTimezone(new Date(task.startAt), input.timezone) : task.startDate || task.dueDate;
    if (date) add(date, task, false);
    if (input.recurrenceProjections && task.recurrence && !task.completed) {
      for (const projection of projectedCalendarDates(task, start, end, input.timezone)) {
        if (projection !== date) add(projection, task, true);
      }
    }
  }
  return entries;
}

function weekday(date: string, width: "short" | "long" = "short"): string {
  return new Intl.DateTimeFormat(undefined, { weekday: width, timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));
}

function dateLabel(date: string): string {
  return new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));
}

function location(task: Todo, input: DashboardCalendarInput): string {
  const root = input.roots.find(item => item.id === task.rootId)?.name || "Space";
  const collection = input.categories.find(item => item.id === task.categoryId)?.name;
  return collection ? `${root} · ${collection}` : root;
}

function agenda(input: DashboardCalendarInput, entries: CalendarEntry[]): string {
  const sorted = [...entries].sort((a, b) => (a.task.startAt || "").localeCompare(b.task.startAt || "") || a.task.title.localeCompare(b.task.title));
  const row = ({ task, projected }: CalendarEntry) => {
    const detail = projected ? "Repeats" : task.startAt ? taskTimeRange(task) : task.dueDate === input.anchor ? "Due" : "Planned";
    return `<li><button type="button" data-dashboard-open="${escapeHtml(task.id)}" ${projected ? 'title="Projected occurrence; open the repeating task"' : ""}><span class="dashboard-calendar__task-title ${task.completed ? "is-complete" : ""}">${escapeHtml(task.title)}</span><small>${escapeHtml(detail)} · ${escapeHtml(location(task, input))}</small></button></li>`;
  };
  const extra = sorted.length > 4 ? `<details class="dashboard-section__more"><summary>Show ${sorted.length - 4} more</summary><ul>${sorted.slice(4).map(row).join("")}</ul></details>` : "";
  return `<div class="dashboard-calendar__agenda"><div class="dashboard-calendar__agenda-head"><strong>${escapeHtml(dateLabel(input.anchor))}</strong><span>${sorted.length ? `${sorted.length} ${sorted.length === 1 ? "task" : "tasks"}` : ""}</span></div>${sorted.length ? `<ul>${sorted.slice(0, 4).map(row).join("")}</ul>${extra}` : `<p>No tasks on this day.</p>`}</div>`;
}

export function dashboardCalendarView(input: DashboardCalendarInput): string {
  const dates = calendarRangeDates(input.anchor, input.mode, input.weekStart);
  const entries = dashboardCalendarEntries(input);
  const period = input.mode === "week" ? `Week of ${input.formatDate(dates[0])}` : calendarPeriodLabel({ anchor: input.anchor, mode: input.mode, weekStart: input.weekStart, formatDate: input.formatDate });
  const modes = (["day", "week", "month"] as const).map(mode => `<button type="button" data-dashboard-calendar-mode="${mode}" aria-pressed="${input.mode === mode}">${mode[0].toUpperCase()}${mode.slice(1)}</button>`).join("");
  const weekdays = input.mode === "month" ? `<div class="dashboard-calendar__weekdays">${dates.slice(0, 7).map(date => `<span>${escapeHtml(weekday(date))}</span>`).join("")}</div>` : "";
  const days = input.mode === "day" ? "" : `<div class="dashboard-calendar__days dashboard-calendar__days--${input.mode}">${dates.map((date, index) => {
    const count = entries.get(date)?.length || 0;
    const classes = [date === input.anchor ? "is-selected" : "", date === input.today ? "is-today" : "", input.mode === "month" && date.slice(0, 7) !== input.anchor.slice(0, 7) ? "is-outside" : ""].filter(Boolean).join(" ");
    const weekNumber = input.mode === "month" && input.weekNumbers && index % 7 === 0 ? `<small class="dashboard-calendar__week-number">W${isoWeekNumber(date)}</small>` : "";
    return `<button class="${classes}" type="button" data-dashboard-calendar-day="${date}" aria-label="${escapeHtml(dateLabel(date))}, ${count} ${count === 1 ? "task" : "tasks"}" aria-pressed="${date === input.anchor}">${input.mode === "week" ? `<span>${escapeHtml(weekday(date))}</span>` : ""}<strong>${Number(date.slice(8))}</strong>${count ? `<small class="dashboard-calendar__count">${count}</small>` : `<small class="dashboard-calendar__count" aria-hidden="true"></small>`}${weekNumber}</button>`;
  }).join("")}</div>`;
  return `<section class="dashboard-section dashboard-calendar" aria-label="Task calendar"><div class="dashboard-calendar__title"><div><h2>Calendar</h2><span>Tasks across spaces</span></div><button type="button" data-view="calendar" aria-label="Open full calendar" title="Open full calendar">${input.icon("expand")}</button></div><div class="dashboard-calendar__controls"><button type="button" data-dashboard-calendar-move="-1" aria-label="Previous ${input.mode}">${input.icon("chevron-left")}</button><strong>${escapeHtml(period)}</strong><button type="button" data-dashboard-calendar-move="0" aria-label="Jump to today">Today</button><button type="button" data-dashboard-calendar-move="1" aria-label="Next ${input.mode}">${input.icon("chevron")}</button></div><div class="dashboard-calendar__modes" role="group" aria-label="Dashboard calendar view">${modes}</div>${weekdays}${days}${agenda(input, entries.get(input.anchor) || [])}</section>`;
}
